import {useMediaQuery} from '@chakra-ui/react';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, Row, UiBox, UiButton, UiImage, UiQuestionnaireLayout, UiSimpleGrid, UiText} from '~/components/elements';
import {AppCarousel} from '~/components/elements/AppCarousel';
import {UiCard} from '~/components/elements/UiCard';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {QUERY_MOBILE} from '~/constants';
import {BIRTH_CONTROL_PATCH, BIRTH_CONTROL_REQUEST_KEYS, PATCH_TOP_ICON} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setSelectedMedication, setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Relation, Option, MedicationItem, GenMedicationItem, Products, PackItem} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire12Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage, addValidationMessage} = useNotification(dispatch);
  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_MEDICATION]?.data,
  );
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_MEDICATION);
  const medicationListAnswer = questionnaire?.options?.find(i => i.value === currentAnswer?.selectedOption)?.data;
  const [medicationList, setMedicationList] = useState<GenMedicationItem[]>(medicationListAnswer);

  const {inputs, setInputs, validation} = useInput({
    selectedOption: currentAnswer?.selectedOption || '',
    selectedMedication: currentAnswer?.selectedMedication || '',
  });
  const isBirthControlPatchSelected = inputs?.selectedOption === BIRTH_CONTROL_PATCH;
  const [selectedItemSlide, setSelectedItemSlide] = useState<Record<string, number> | null>(null);
  const hasSelectedSlideItem = selectedItemSlide && !!Object.keys(selectedItemSlide).length;

  const onChangeCarousel = (pos: number, title: string) => {
    setSelectedItemSlide({[title]: pos});
  };

  const onClickOption = (answer: Option) => {
    const {value} = answer;
    setInputs({selectedOption: value, selectedMedication: ''});
    if (answer?.data) setMedicationList(answer.data);
  };

  const onCLickMedication = (item: Products) => {
    setInputs({selectedMedication: item.value});
    dispatch(setSelectedMedication({data: item}));
  };

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_MEDICATION,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );

    dispatch(setNextQuestionnaire({value: BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_PRODUCT_PACKAGE}));
  };

  const renderCardItem = (data: Products, minWidth: string = '200px', isPatchSelected: boolean = false) => (
    <UiCard
      key={data.value}
      variant="rounded"
      py={spacing.small}
      px={spacing.tiny}
      boxShadow={inputs.selectedMedication === data.value ? 'outline' : 'xl'}
      onClick={!!data?.outOfStock ? undefined : () => onCLickMedication(data)}
      _hover={!!data?.outOfStock ? {pointerEvents: 'none'} : {cursor: 'pointer'}}
      mx={4}
      my={6}
      minWidth={minWidth}
      transition="300ms"
    >
      <UiText fontSize="lg" fontWeight="600">
        {data?.name}
      </UiText>
      <UiText fontSize="sm" fontWeight="600">
        {data?.subName}
      </UiText>
      <UiImage
        type="icon"
        name={data?.icon}
        boxSize={isPatchSelected ? spacing.mLarge : spacing.large}
        objectFit="contain"
      />
      <Row alignItems="center">
        <UiText fontWeight="600" fontSize={{base: '0.8em', md: 'sm', lg: 'sm'}}>
          from &nbsp;
        </UiText>
        <UiText fontSize={{base: 'md', md: 'xl', lg: 'xl'}} fontWeight="600">
          {data?.unit}
          {data?.price}*
        </UiText>
        <UiText fontWeight="600" fontSize={{base: '0.8em', md: 'sm', lg: 'sm'}}>
          &nbsp; / pack
        </UiText>
      </Row>
      <UiBox>
        {data?.description?.map((des, index) => (
          <UiText style={!!data?.outOfStock ? {color: 'red'} : undefined} key={index} lineHeight="15px" fontSize="10px">
            {des}
          </UiText>
        ))}
      </UiBox>
    </UiCard>
  );

  const renderMedicationItem = (products: Products[], title: string = '') => {
    // const isDesktopSmallItems = isDesktop && products?.length < 3 && !isBirthControlPatchSelected;
    const centerSlidePercentage = isDesktop && inputs?.selectedOption ? 33.3 : 65;

    if (isBirthControlPatchSelected) {
      return (
        <Row w="100%" justifyContent="center">
          {products?.map(product => renderCardItem(product, '200px', isBirthControlPatchSelected))}
        </Row>
      );
    }

    if (isDesktop) {
      return (
        <UiSimpleGrid columns={3} spacingY={0} spacingX={1} w="100%">
          {products?.map(product => renderCardItem(product, '200px'))}
        </UiSimpleGrid>
      );
    }

    return (
      <AppCarousel
        selectedItem={hasSelectedSlideItem && selectedItemSlide[title] >= 0 ? selectedItemSlide[title] : 0}
        onChange={idx => onChangeCarousel(idx, title)}
        onClickItem={index => onChangeCarousel(index, title)}
        showIndicators={false}
        showArrows={false}
        showStatus={false}
        showThumbs={false}
        centerMode
        centerSlidePercentage={centerSlidePercentage}
        emulateTouch
        preventMovementUntilSwipeScrollTolerance={true}
        swipeScrollTolerance={50}
      >
        {products?.map(product => renderCardItem(product, '40%'))}
      </AppCarousel>
    );
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedOption || !inputs.selectedMedication}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      hasFloatActionButtons
    >
      <Col alignItems="center" textAlign="center">
        <UiImage
          type="icon"
          name={isBirthControlPatchSelected ? PATCH_TOP_ICON : questionnaire?.icon}
          boxSize={spacing.mLarge}
          mt={spacing.small}
        />
        <UiText fontSize="lg" fontWeight="bold" mt={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={4}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                scrollAmount={300}
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => onClickOption(value)}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {!!inputs.selectedOption &&
          questionnaire?.descriptions?.default?.map(i => (
            <UiText key={i} fontSize="xs" color="fadedJade" mt={spacing.xmedium}>
              {i}
            </UiText>
          ))}
        <UiBox mt={spacing.none} w="100%">
          {medicationList?.map((item, index) => {
            return (
              <Col key={index}>
                <UiText
                  ml={4}
                  textAlign="left"
                  fontSize="lg"
                  fontWeight={500}
                  lineHeight="22px"
                  color="liteOuter"
                  mt={spacing.small}
                >
                  {item?.title}
                </UiText>
                {renderMedicationItem(item?.products, item?.title)}
              </Col>
            );
          })}
        </UiBox>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire12Screen;
